/**
 * Signing in, and the one online step that makes the rest offline.
 *
 * Specification: ../test/shift-client.spec.ts. Master plan §3 K2.
 *
 * THE SHAPE IS FORCED BY THE STANDARD. NIST SP 800-63B-4 requires a verifier to
 * store memorized secrets in a form resistant to offline attack, so the server
 * holds an Argon2id hash and cannot produce anybody's PIN. The only moment a
 * device-bound verifier can be computed is the moment somebody types theirs.
 *
 * So: the FIRST sign-in for a person on a device is online — the PIN goes to
 * the server once, over TLS, and comes back as a verifier this device keeps.
 * EVERY SIGN-IN AFTER THAT IS LOCAL, which is what matters: the WAN being down
 * is when a till matters most, and that is the ordinary case rather than the
 * first-ever one.
 *
 * The cached credential carries no PIN and no salt. The salt is derived from a
 * secret only this device holds and is never transmitted, so a credential
 * lifted off a stolen tablet cannot even generate candidates. archive/v0
 * shipped the whole staff PIN table to every paired device.
 */

import {
  PinAttemptLimiter,
  verifyStaffPin,
  type PinResult,
  type ShiftCredentialBundle,
  type StaffCredential,
} from "@gf/kernel/staff-auth";

export class ShiftFetchError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = "ShiftFetchError";
  }
}

export interface RosteredPerson {
  readonly staffId: string;
  readonly displayName: string;
  readonly roleId: string;
  /** Moves with a new PIN, a new role or leaving. See `installRoster`. */
  readonly epoch?: number;
}

export interface ShiftEndpointArgs {
  readonly baseUrl: string;
  readonly apiKey: string;
  readonly venueId: string;
  readonly deviceId: string;
  readonly fetch?: typeof fetch;
}

/** Names and roles, for the picker. No verifiers, no PINs. */
export async function fetchRoster(args: ShiftEndpointArgs): Promise<RosteredPerson[]> {
  const get = args.fetch ?? fetch;
  const url = `${args.baseUrl}/v1/shift/roster?venueId=${encodeURIComponent(args.venueId)}`;

  let response: Response;
  try {
    response = await get(url, { headers: { authorization: `Bearer ${args.apiKey}` } });
  } catch (error) {
    throw new ShiftFetchError("the shift service is unreachable", { cause: error });
  }
  if (!response.ok) throw new ShiftFetchError(`the shift service answered ${response.status}`);

  const body = (await response.json()) as { staff?: RosteredPerson[] };
  if (!Array.isArray(body.staff)) throw new ShiftFetchError("the roster has no staff");
  return body.staff;
}

export interface EnrolResult {
  readonly scopeId: string;
  readonly expiresAt: string;
  readonly credential: StaffCredential;
}

/**
 * Exchange a PIN for a device-bound verifier. Once, per person, per device.
 *
 * The PIN leaves the device exactly here and nowhere else.
 */
export async function enrolOnDevice(
  args: ShiftEndpointArgs & { staffId: string; pin: string },
): Promise<EnrolResult> {
  const send = args.fetch ?? fetch;

  let response: Response;
  try {
    response = await send(`${args.baseUrl}/v1/shift/enrol`, {
      method: "POST",
      headers: {
        authorization: `Bearer ${args.apiKey}`,
        "content-type": "application/json",
        /**
         * FRESH PER ATTEMPT, unlike the event uploader's, which is derived from
         * its batch.
         *
         * The platform requires a key on every mutating request, and the client
         * did not send one — so enrolment answered 400 and nobody could have
         * signed in at all. Only running it found that.
         *
         * It must be random here. A key derived from (device, staff) would let
         * the middleware replay a cached 401 from a mistyped PIN, so the right
         * PIN typed immediately afterwards would be refused with the previous
         * answer. And enrolment has no side effect to duplicate: the verifier
         * is derived deterministically and no row is written, so re-processing
         * a retry is free.
         */
        "idempotency-key": crypto.randomUUID(),
      },
      body: JSON.stringify({
        venueId: args.venueId,
        deviceId: args.deviceId,
        staffId: args.staffId,
        pin: args.pin,
      }),
    });
  } catch (error) {
    throw new ShiftFetchError("the shift service is unreachable", { cause: error });
  }

  if (response.status === 401) throw new ShiftAuthError("enrolment refused");
  // The server holds PIN guesses back for a while: a wait, not a wrong PIN.
  if (response.status === 429) throw new ShiftAuthError("held", Number(response.headers.get("retry-after")) || 60);
  if (!response.ok) throw new ShiftFetchError(`the shift service answered ${response.status}`);

  return (await response.json()) as EnrolResult;
}

/** A refusal, as distinct from the server being unreachable. */
export class ShiftAuthError extends Error {
  constructor(
    message: string,
    /** Set when the server says to wait rather than that the PIN was wrong. */
    readonly retryAfterSeconds?: number,
  ) {
    super(message);
    this.name = "ShiftAuthError";
  }
}

/** The message key a refusal maps to, so no English reaches a screen. */
const REJECTION_KEYS = {
  BAD_PIN: "pos.login.badPin",
  NOT_ROSTERED: "pos.login.notRostered",
  EXPIRED: "pos.login.expired",
} as const;

export interface SignInOutcome {
  readonly ok: boolean;
  readonly staffId?: string;
  readonly displayName?: string;
  readonly roleId?: string;
  /** A message key, never a sentence. */
  readonly messageKey?: string;
  /** Seconds until this staff member may try again, or 0. */
  readonly lockedForSeconds: number;
}

/**
 * The message key a refusal maps to, so no English reaches a screen.
 */
const NEEDS_NETWORK = "pos.login.needsNetwork";

/**
 * Sign in, with per-staff rate limiting.
 *
 * PER STAFF MEMBER, not per device. Locking the device would let one person
 * guessing at a colleague's PIN take the whole till out of service in the
 * middle of a rush, which is a denial of service anybody on the floor could
 * trigger by accident.
 */
export class ShiftSession {
  readonly #limiter: PinAttemptLimiter;
  readonly #deviceSecret: string;
  readonly #enrol: (staffId: string, pin: string) => Promise<EnrolResult>;
  #roster: readonly RosteredPerson[] = [];
  /** Whether the server has answered at all — an unknown roster prunes nothing. */
  #rosterKnown = false;
  #credentials: ShiftCredentialBundle | null = null;

  constructor(args: {
    readonly deviceSecret: string;
    /** Performs the one online step. Injected so the flow is testable. */
    readonly enrol: (staffId: string, pin: string) => Promise<EnrolResult>;
    readonly limiter?: PinAttemptLimiter;
  }) {
    this.#deviceSecret = args.deviceSecret;
    this.#enrol = args.enrol;
    this.#limiter = args.limiter ?? new PinAttemptLimiter({ maxAttempts: 5, lockoutMs: 60_000 });
  }

  /** Names for the picker. Cached, so the login screen works offline. */
  /**
   * The server's word on who works here — and so which credentials this device
   * may still trust. Somebody no longer on it, or whose epoch has moved (a
   * reset PIN, a new role), is forgotten: a PIN is checked offline, and without
   * this the old one opened the drawer until the credential expired a month
   * later. A credential minted before epochs existed is forgotten too; the next
   * sign-in, online, mints a fresh one.
   */
  installRoster(roster: readonly RosteredPerson[]): void {
    this.#roster = roster;
    this.#rosterKnown = true;
    this.#prune();
  }

  /**
   * Credentials this device has already minted. Survives a restart once
   * persisted — and is held to the roster too, because a stored set can be
   * restored AFTER one arrived.
   */
  installCredentials(bundle: ShiftCredentialBundle): void {
    this.#credentials = bundle;
    this.#prune();
  }

  #prune(): void {
    if (!this.#credentials || !this.#rosterKnown) return;
    const current = new Map(this.#roster.map((p) => [p.staffId, p]));
    this.#credentials = {
      ...this.#credentials,
      credentials: this.#credentials.credentials.filter((c) => {
        const person = current.get(c.staffId);
        return person !== undefined && (person.epoch === undefined || person.epoch === c.epoch);
      }),
    };
  }

  credentials(): ShiftCredentialBundle | null {
    return this.#credentials;
  }

  /**
   * Who the sign-in screen offers.
   *
   * THE ROSTER FIRST, THE CREDENTIAL BUNDLE WHEN THERE IS NO ROSTER — and that
   * fallback is what makes an outage survivable.
   *
   * The roster arrives over the network. The bundle is on the device, holds
   * exactly the people who can authenticate on it, and carries their names and
   * roles for precisely this. Reading only the roster meant a till reloaded
   * during an outage came up with an EMPTY sign-in screen: credentials intact
   * in OPFS, nobody listed, nobody able to sign in — during the outage the
   * whole offline story exists for.
   *
   * Deriving the picker from the bundle also removes a way for the two to
   * disagree: a name on this screen is now, by construction, somebody this
   * device can actually verify.
   */
  candidates(): readonly RosteredPerson[] {
    const source =
      this.#roster.length > 0
        ? this.#roster
        : (this.#credentials?.credentials ?? []);

    return source.map((p) => ({
      staffId: p.staffId,
      displayName: p.displayName,
      roleId: p.roleId,
    }));
  }

  #enrolled(staffId: string): boolean {
    return (this.#credentials?.credentials ?? []).some((c) => c.staffId === staffId);
  }

  async signIn(staffId: string, pin: string, now: Date): Promise<SignInOutcome> {
    const lockedUntil = this.#limiter.lockedUntil(staffId, now);
    if (lockedUntil !== null) {
      return {
        ok: false,
        messageKey: "pos.login.lockedOut",
        lockedForSeconds: Math.ceil((lockedUntil - now.getTime()) / 1000),
      };
    }

    /**
     * Already enrolled on this device: verify LOCALLY, no network.
     *
     * This is the ordinary case and the one that has to work during an outage.
     */
    if (this.#credentials && this.#enrolled(staffId)) {
      const result: PinResult = await verifyStaffPin(
        this.#credentials,
        staffId,
        pin,
        this.#deviceSecret,
        now,
      );

      if (result.ok) {
        this.#limiter.recordSuccess(staffId);
        return {
          ok: true,
          staffId: result.staff.staffId,
          displayName: result.staff.displayName,
          roleId: result.staff.roleId,
          lockedForSeconds: 0,
        };
      }

      // An EXPIRED credential is not a wrong PIN: re-enrol rather than refuse,
      // so a month-old credential lapsing does not lock somebody out of a till
      // they are standing in front of.
      if (result.reason !== "EXPIRED") {
        if (result.reason === "BAD_PIN") this.#limiter.recordFailure(staffId, now);
        return { ok: false, messageKey: REJECTION_KEYS[result.reason], lockedForSeconds: 0 };
      }
    }

    /**
     * Not enrolled here yet — the one online step.
     *
     * Unreachable server means the till says so rather than "wrong PIN". The
     * remedies are completely different and a server who is told the wrong one
     * types their PIN harder.
     */
    try {
      const enrolled = await this.#enrol(staffId, pin);
      const merged = (this.#credentials?.credentials ?? []).filter(
        (c) => c.staffId !== staffId,
      );

      this.#credentials = {
        shiftId: enrolled.scopeId,
        deviceId: this.#credentials?.deviceId ?? "",
        venueId: this.#credentials?.venueId ?? "",
        expiresAt: enrolled.expiresAt,
        credentials: [...merged, enrolled.credential],
      };
      this.#limiter.recordSuccess(staffId);

      return {
        ok: true,
        staffId: enrolled.credential.staffId,
        displayName: enrolled.credential.displayName,
        roleId: enrolled.credential.roleId,
        lockedForSeconds: 0,
      };
    } catch (error) {
      if (error instanceof ShiftAuthError && error.retryAfterSeconds) {
        return { ok: false, messageKey: "pos.login.lockedOut", lockedForSeconds: error.retryAfterSeconds };
      }
      if (error instanceof ShiftAuthError) {
        this.#limiter.recordFailure(staffId, now);
        return { ok: false, messageKey: "pos.login.badPin", lockedForSeconds: 0 };
      }
      // The server is not there. Not the server's fault and not the staff
      // member's, so it costs nobody an attempt.
      return { ok: false, messageKey: NEEDS_NETWORK, lockedForSeconds: 0 };
    }
  }
}
